<script setup lang="ts">
/**
 * /login — sign-in form for Meta App Review reviewers (api/app/reviewer.py, owner 19 ต.ค. 69: Meta tests the chat without
 * the POS). LoginGate.vue shows it only on /login, signed out, while the owner has it switched on (otherwise the POS
 * screen, SignedOut.vue). English first (for Meta), Thai under it. Success → `signedIn` (App.vue reloads /me and opens
 * the inbox) · switched off meanwhile → `closed` (the gate shows the POS screen).
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { reviewerPublicApi, type SignInResult } from '../api/reviewer'
import type { SignedOutReason } from '../api/session'
import logo from '../assets/cleanfit-logo.webp'
import Icon from './ui/Icon.vue'

const props = defineProps<{ reason?: SignedOutReason }>()
const emit = defineEmits<{ signedIn: []; closed: [] }>()

const username = ref('')
const password = ref('')
const show = ref(false)
const busy = ref(false)
const result = ref<SignInResult | null>(null)

onMounted(() => {
  // the app is desktop-first (body min-width 1280px, styles/base.css); this one card must fit a phone
  document.body.style.minWidth = '0'
})
onBeforeUnmount(() => {
  document.body.style.minWidth = ''
})

const ERRORS: Partial<Record<SignInResult, [string, string]>> = {
  wrong: ['Incorrect username or password.', 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง'],
  locked: ['Too many attempts. Please wait 15 minutes, then try again.', 'ลองหลายครั้งเกินไป รอ 15 นาทีแล้วลองใหม่'],
  invalid: ['Enter the username and the password.', 'ใส่ชื่อผู้ใช้และรหัสผ่าน'],
  origin: ['Please sign in from this page.', 'เข้าระบบจากหน้านี้เท่านั้น'],
  error: ['Cannot reach the server. Check the connection and try again.', 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองอีกครั้ง'],
}
const error = computed(() => (result.value ? ERRORS[result.value] ?? null : null))
const note = computed<[string, string] | null>(() =>
  props.reason === 'logged_out'
    ? ['You have signed out.', 'ออกจากระบบแล้ว']
    : props.reason === 'session'
      ? ['Your session has ended. Please sign in again.', 'หมดเวลาการเข้าระบบ เข้าใหม่อีกครั้ง']
      : null,
)

async function submit(): Promise<void> {
  if (busy.value) return
  if (!username.value.trim() || !password.value) {
    result.value = 'invalid'
    return
  }
  busy.value = true
  result.value = null
  try {
    const r = await reviewerPublicApi().signIn(username.value.trim(), password.value)
    if (r === 'ok') {
      password.value = ''
      emit('signedIn')
      return
    }
    if (r === 'closed') {
      emit('closed') // switched off meanwhile
      return
    }
    result.value = r
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <main class="gate">
    <section class="card" aria-labelledby="rv-title">
      <img class="mark" :src="logo" alt="Cleanfit" width="64" height="64" />
      <h1 id="rv-title">Sign in</h1>

      <form class="form" novalidate @submit.prevent="submit">
        <p v-if="note" class="note" role="status">{{ note[0] }} <span class="th">{{ note[1] }}</span></p>
        <label class="rv-field" for="rv-user">
          <span class="rv-lb">Username <span class="th">ชื่อผู้ใช้</span></span>
          <input
            id="rv-user"
            v-model="username"
            type="text"
            name="username"
            autocomplete="username"
            autocapitalize="none"
            spellcheck="false"
            :aria-invalid="result === 'wrong' || (result === 'invalid' && !username.trim())"
            @input="result = null"
          />
        </label>
        <label class="rv-field" for="rv-pass">
          <span class="rv-lb">Password <span class="th">รหัสผ่าน</span></span>
          <span class="pw">
            <input
              id="rv-pass"
              v-model="password"
              :type="show ? 'text' : 'password'"
              name="password"
              autocomplete="current-password"
              autocapitalize="none"
              spellcheck="false"
              :aria-invalid="result === 'wrong' || (result === 'invalid' && !password)"
              @input="result = null"
            />
            <button
              class="eye"
              type="button"
              :aria-label="show ? 'Hide password' : 'Show password'"
              :aria-pressed="show"
              @click="show = !show"
            >
              <Icon :name="show ? 'eyeOff' : 'eye'" />
            </button>
          </span>
        </label>
        <p v-if="error" class="err" role="alert">
          <Icon name="alertCircle" />
          <span>{{ error[0] }} <span class="th">{{ error[1] }}</span></span>
        </p>
        <button class="go" type="submit" :disabled="busy">
          {{ busy ? 'Signing in…' : 'Sign in' }}
          <span class="go-th">{{ busy ? 'กำลังเข้าระบบ' : 'เข้าระบบ' }}</span>
        </button>
      </form>
    </section>
  </main>
</template>

<style scoped>
.gate {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background: var(--bg);
}
.card {
  width: 100%;
  max-width: 420px;
  background: var(--sf);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 32px 32px 28px;
  box-shadow: 0 1px 2px rgba(23, 33, 28, 0.04);
}
.mark {
  display: block;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 16px;
}
h1 {
  text-align: center;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.45;
  color: var(--ink);
}
.th {
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  color: var(--mut);
}
.form {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.note {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f8f6;
  font-size: 13px;
  line-height: 1.5;
  color: #3e4d46;
}
.note .th {
  display: inline;
  font-size: 12.5px;
}
.rv-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rv-lb {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
}
.rv-lb .th {
  display: inline;
  margin-left: 6px;
  font-size: 12.5px;
}
input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  font: inherit;
  font-size: 16px; /* no zoom-in on iOS */
  line-height: 22px;
  color: var(--ink);
}
input:focus {
  outline: 2px solid var(--ac);
  outline-offset: -1px;
  border-color: transparent;
}
input[aria-invalid='true'] {
  border-color: #d9534f;
}
.pw {
  position: relative;
  display: block;
}
.pw input {
  padding-right: 48px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  letter-spacing: 0.02em;
}
.eye {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
}
.eye:hover {
  background: #f1f5f3;
}
.eye :deep(.ic) {
  width: 18px;
  height: 18px;
}
.err {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--crits);
  color: #8f1d14;
  font-size: 13.5px;
  line-height: 1.5;
}
.err .th {
  color: #8f1d14;
  font-size: 12.5px;
}
.err :deep(.ic) {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 2px;
}
.go {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 20px;
  border-radius: 10px;
  background: var(--ac);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
}
.go:hover:not(:disabled) {
  background: var(--ac2);
}
.go:disabled {
  opacity: 0.7;
  cursor: default;
}
.go-th {
  font-size: 13px;
  font-weight: 400;
  opacity: 0.85;
}
@media (max-width: 420px) {
  .card {
    padding: 24px 20px 22px;
  }
}
</style>
